import type { EditorialContent } from './editorial.types';
import type { Enum } from './enum.types';

export type UefatvAuthResponse = {
  authorisationToken: string;
  refreshToken: string;
};

export type UefatvResponse = {
  heroes: Hero[];
  buckets: Bucket[];
  paging: {
    lastSeen: string;
    moreDataAvailable: boolean;
  };
};

export type Hero = {
  heroId: number;
  ctaText: string;
  description: string;
  enabled: boolean;
  imageUrl: string;
  channelLogo: string;
  title: string;
  titleImage: string;
  textAlignment: 'RIGHT' | 'CENTER' | 'LEFT';
  alignmentHorizontal: 'RIGHT' | 'CENTER' | 'LEFT';
  alignmentVertical: 'TOP' | 'MIDDLE' | 'BOTTOM';
  link: Link;
  primaryButton: { ctaText: string; link: Link };
  secondaryButton: { ctaText: string; link: Link };
  background: { imageUrl: string; portraitImageUrl?: string };
};

export type Bucket = {
  exid?: string;
  name?: string;
  page?: number;
  type?: BucketType;
  rowTypeData?: RowTypeData;
  resultsPerPage?: number;
  totalPages?: number;
  totalResults?: number;
  contentList?: (PlaylistItem | VodItem)[];
  paging?: {
    lastSeen: string;
    moreDataAvailable: boolean;
  };
};

export type BaseContentItem = {
  id?: number;
  type?: MediaType;
  accessLevel?: AccessLevel;
  description?: string;
  posterUrl?: string;
  thumbnailUrl?: string;
  thumbnailsPreview?: string;
  title?: string;
  sectionName?: string;
};

export type VodItem = BaseContentItem & {
  computedReleases: string[];
  contentDownload: { permission: 'ALLOWED' | 'DISALLOWED' };
  duration: number;
  favourite: boolean;
  licenceIds: string[];
  maxHeight: number;
  offlinePalybackLanguages: string[];
  onlinePlayback: string;
  playbackType: string;
  subEvents: string[];
  watchContext: string[];
  watchStatus: string;
};
export type PlaylistItem = BaseContentItem & {
  coverUrl?: string;
  smallCoverUrl?: string;
  displayableTags?: string[];
  licencesIds?: string[];
  playlistType?: PlaylistType;
  upcomingReleases?: string[];
  vodCount?: number;
  vods?: VodItem[];
  paging?: {
    lastSeen: string;
    moreDataAvailable: boolean;
  };
};

export type VOD = {
  videoId: number;
  playerUrlCallback: string;
  tags: string[];
};

export type Link = { type: LinkType; event: Event; url?: string };

export type Event = {
  accessLevel: AccessLevel;
  coverUrl: string;
  description: string;
  id: number;
  playlistType: PlaylistType;
  posterUrl: string;
  smallCoverUrl: string;
  title: string;
  type: MediaType;
  vodCount: number;
};

export const LinkType = {
  CONTENTLINK: 'ContentLink',
  EXTERNALLINK: 'ExternalLink',
} as const;
export type LinkType = Enum<typeof LinkType>;

export const AccessLevel = {
  GRANTED: 'GRANTED',
  DENIED: 'DEINED',
  GEOBLOCKED: 'GEOBLOCKED',
  GRANTED_ON_SIGN: 'GRANTED_ON_SIGN',
} as const;
export type AccessLevel = Enum<typeof AccessLevel>;

export const PlaylistType = {
  BASIC: 'BASIC',
  FEATURED: 'FEATURED',
  INLINE: 'INLINE',
} as const;
export type PlaylistType = Enum<typeof PlaylistType>;

export const BucketType = {
  PLAYLISTS: 'PLAYLISTS',
  VOD_VIDEO: 'VOD_VIDEO',
  LIVE: 'LIVE',
  SECTION_LINK: 'SECTION_LINK',
  SERIES: 'SERIES',
} as const;
export type BucketType = Enum<typeof BucketType>;

export const MediaType = {
  PLAYLIST: 'PLAYLIST',
  VOD: 'VOD',
  LIVE: 'LIVE',
  SECTION_LINK: 'SECTION_LINK',
  EPG: 'EPG',
} as const;
export type MediaType = Enum<typeof MediaType>;

export type RowTypeData = {
  contentDescription?: 'HIDE' | 'SHOW';
  hideMetadata?: boolean;
  rowType: PlaylistType;
  playlistImageType: 'NONE' | 'POSTER';
  rowCount: number;
  title: string;
  description?: string;
  displayPosters: boolean;
  background: {
    animationType: string;
    gradientBottom: boolean;
    gradientLeft: boolean;
    gradientRight: boolean;
    gradientTop: boolean;
  };
};

export type UefatvEditorialContent = EditorialContent & {
  vodCount?: number;
  exid?: string;
  lastSeen?: string;
  moreDataAvailable?: boolean;
};

export enum VideoInteractionType {
  MANUAL = 'manual',
  AUTO = 'auto',
}
